import { useState } from "react";

import { Callout, Classes, Dialog } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { DefaultButton } from "@/components/pieces/button/DefaultButton";
import { PrimaryButton } from "@/components/pieces/button/PrimaryButton";
import {
  useCancelUnfulfilledPaidOrderMutation,
  useCancelUnpaidOrderMutation,
} from "@/graphql";

interface CancelUnfulfilledOrderConfirmDialogProps {
  orderId?: string;
  customerId?: string;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isPaidOrder: boolean;
}

export function CancelUnfulfilledOrderConfirmDialog({
  orderId,
  customerId,
  isOpen,
  onCancel,
  onConfirm,
  isPaidOrder = true,
}: CancelUnfulfilledOrderConfirmDialogProps) {
  const [error, setError] = useState<string | null>(null);

  const onError = (error?: Error) => {
    setError(
      error?.message ??
        "An error occurred while canceling the order. Please contact support if the issue persists.",
    );
  };

  const [processCancelUnpaid, { loading: cancelingUnpaid }] =
    useCancelUnpaidOrderMutation({
      variables: {
        orderId: orderId ?? "",
        customerId: customerId ?? "",
      },
      onCompleted: ({ cancelUnpaidOrder }) => {
        if (cancelUnpaidOrder?.ok) {
          onConfirm();
          setError(null);
        } else {
          onError(new Error(cancelUnpaidOrder?.error?.message ?? ""));
        }
      },
      onError,
    });

  const [processCancelUnfulfilledPaid, { loading: cancelingUnfulfilled }] =
    useCancelUnfulfilledPaidOrderMutation({
      variables: {
        orderId: orderId ?? "",
        customerId: customerId ?? "",
      },
      onCompleted: ({ cancelUnfulfilledPaidOrder }) => {
        if (cancelUnfulfilledPaidOrder?.ok) {
          onConfirm();
          setError(null);
        } else {
          onError(new Error(cancelUnfulfilledPaidOrder?.error?.message ?? ""));
        }
      },
      onError,
    });

  const handleConfirm = () => {
    if (isPaidOrder) {
      processCancelUnfulfilledPaid();
    } else {
      processCancelUnpaid();
    }
  };

  const loading = cancelingUnpaid || cancelingUnfulfilled;

  return (
    <>
      <Dialog
        isOpen={isOpen}
        icon="trash"
        title={<Box py={2}>Confirm cancelation?</Box>}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        style={{ maxWidth: 550, width: "95%" }}
        onClose={onCancel}
      >
        <Box p={24}>
          <Box
            className={`${Classes.DIALOG_BODY} ${Classes.RUNNING_TEXT}`}
            mb={3}
          >
            Are you sure you want to cancel this order?
          </Box>
          {error && (
            <Callout intent="danger" className={Classes.RUNNING_TEXT}>
              {error}
            </Callout>
          )}
          <Flex mt={3} className={Classes.DIALOG_FOOTER}>
            <Box>
              <DefaultButton disabled={loading} onClick={onCancel}>
                Nevermind
              </DefaultButton>
            </Box>
            <Box ml={3}>
              <PrimaryButton
                intent="primary"
                disabled={loading}
                loading={loading}
                onClick={handleConfirm}
              >
                Yes, cancel
              </PrimaryButton>
            </Box>
          </Flex>
        </Box>
      </Dialog>
    </>
  );
}
